import Box from "@mui/system/Box";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css"; // Theme
import {
  CellKeyDownEvent,
  ColDef,
  FullWidthCellKeyDownEvent,
  GetRowIdParams,
  RowClassParams,
  RowClickedEvent,
  RowStyle,
  ColumnState,
} from "ag-grid-community";
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from "react";
import "@/styles/ag-data-grid.css";
import QuoteLoader from "../Loader/QuoteLoader";
import { DEFAULT_PAGE_SIZES } from "@/types/constants/common";
import Stack from "@mui/joy/Stack";
import ColumnsConfig from "./ColumnsConfig";
import ExportToCSV from "./ExportToCSV";

type Props<T> = (
  | {
      hidePagination?: true;
      pageSize?: never;
      pageSizes?: never[];
    }
  | {
      hidePagination?: false;
      pageSize?: number;
      pageSizes?: number[];
    }
) & {
  columnDefs: ColDef<T>[];
  rowData: T[];
  sortable?: boolean;
  idProperty: string;
  emptyText?: string;
  rowHeight?: number;
  csvFileName?: string;
  hideCsvExport?: boolean;
  hideColumnsConfig?: boolean;
  quickFilterText?: string;
  quickFilterParser?: any;
  allColumns?: boolean;
  multiSelectRow?: boolean;
  pinnedBottomRowData?: any[];
  onSelectionChanged?: (selected: T[]) => void;
  // Rows that can't be ticked, including by the header's select-all
  isRowSelectable?: (row: T) => boolean;
  domLayout?: "autoHeight" | "normal" | "print";
  removeMinHeight?: boolean;
  getRowStyle?: (params: RowClassParams<T>) => RowStyle | undefined;
  // Makes rows clickable (with hover highlight); clicks on buttons, links and checkboxes inside a row are ignored
  onRowClicked?: (row: T) => void;
  /** Called after someone resizes, moves, hides or pins a column, with the grid's column state, e.g. to remember it */
  onColumnStateChanged?: (state: ColumnState[]) => void;
};

const LocalDataGridInner = <T,>(
  {
    columnDefs,
    rowData,
    pageSize,
    quickFilterText,
    csvFileName,
    idProperty,
    pageSizes,
    sortable,
    domLayout,
    onSelectionChanged,
    isRowSelectable,
    multiSelectRow,
    quickFilterParser,
    pinnedBottomRowData,
    getRowStyle,
    onRowClicked,
    onColumnStateChanged,
    emptyText = "No data found",
    allColumns = true,
    hideCsvExport = false,
    hidePagination = false,
    hideColumnsConfig = false,
    rowHeight = 36,
    removeMinHeight = false,
  }: Props<T>,
  ref: any,
) => {
  const agGridRef = useRef<AgGridReact>(null);

  const columns = useMemo(() => columnDefs, [columnDefs]);

  useImperativeHandle(
    ref,
    () => {
      return {
        clearRowSelections: () => {
          agGridRef.current?.api.deselectAll();
        },
        // Clears every column's filter (the floating filter boxes too)
        clearFilters: () => {
          agGridRef.current?.api.setFilterModel(null);
        },
        // Back to the column definitions: visibility, width, pinning and order
        resetColumnState: () => {
          agGridRef.current?.api.resetColumnState();
        },
        // Rows in the order shown, after sorting and filtering, e.g. to step through them one by one
        getDisplayedRows: () => {
          const rows: T[] = [];
          agGridRef.current?.api.forEachNodeAfterFilterAndSort((node) => node.data && rows.push(node.data));
          return rows;
        },
      };
    },
    [agGridRef.current],
  );

  const defaultColDef: ColDef<T> = useMemo(
    () => ({
      autoHeight: true,
      sortable: sortable || false,
      unSortIcon: true,
    }),
    [sortable],
  );

  const getRowId = useCallback(
    (props: GetRowIdParams<T>) => {
      return (props.data as any)[idProperty];
    },
    [idProperty],
  );
  const changeVisibility = useCallback(
    (columnId: string, visible: boolean) => {
      if (agGridRef.current) {
        agGridRef.current.api.setColumnVisible(columnId, visible);
      }
    },
    [agGridRef.current],
  );

  const exportToCSV = useCallback(() => {
    if (agGridRef.current) {
      agGridRef.current.api.exportDataAsCsv({
        fileName: csvFileName,
        allColumns: allColumns,
      });
    }
  }, [agGridRef.current, csvFileName, allColumns]);

  return (
    <Stack
      direction={"row"}
      gap={1}
      sx={{
        height: "100%",
        flex: "1 1 0%",
        width: "100%",
        position: "relative",
      }}
    >
      <Box
        className={`ag-theme-quartz ${removeMinHeight ? "remove-ag-min-height" : ""}`}
        sx={{
          flex: "1 1 0%",
          height: "100%",
        }}
      >
        <AgGridReact
          ref={agGridRef}
          rowData={rowData}
          columnDefs={columns}
          getRowId={getRowId}
          rowHeight={rowHeight}
          popupParent={document.body}
          defaultColDef={defaultColDef}
          enableCellTextSelection
          suppressRowClickSelection
          suppressRowDeselection
          // Clickable rows need keyboard focus, so they can be opened with Enter as well as a click
          suppressCellFocus={!onRowClicked}
          onCellKeyDown={
            onRowClicked
              ? (event: CellKeyDownEvent<T> | FullWidthCellKeyDownEvent<T>) => {
                  const keyEvent = event.event as KeyboardEvent | null;
                  // Enter on a button or link inside the row is for that control, as with clicks
                  const target = keyEvent?.target as HTMLElement | null;
                  if (target?.closest("button, a, input, [role='menu'], .ag-selection-checkbox")) return;
                  // Enter only: Space ticks the row's checkbox in grids with selection
                  if (keyEvent?.key === "Enter" && event.data) onRowClicked(event.data);
                }
              : undefined
          }
          // Only changes a person makes count; columnDefs updates from the page don't
          onColumnResized={(e) =>
            onColumnStateChanged &&
            e.finished &&
            e.source === "uiColumnResized" &&
            onColumnStateChanged(e.api.getColumnState())
          }
          onColumnMoved={(e) =>
            onColumnStateChanged &&
            e.finished &&
            e.source === "uiColumnMoved" &&
            onColumnStateChanged(e.api.getColumnState())
          }
          onColumnVisible={(e) =>
            onColumnStateChanged &&
            ["api", "toolPanelUi", "columnMenu"].includes(e.source) &&
            onColumnStateChanged(e.api.getColumnState())
          }
          onColumnPinned={(e) =>
            onColumnStateChanged &&
            ["api", "toolPanelUi", "columnMenu", "contextMenu"].includes(e.source) &&
            onColumnStateChanged(e.api.getColumnState())
          }
          stopEditingWhenCellsLoseFocus
          suppressRowHoverHighlight={!onRowClicked}
          suppressMultiSort
          domLayout={domLayout}
          quickFilterText={quickFilterText}
          pagination={!hidePagination}
          paginationPageSize={pageSize}
          paginationPageSizeSelector={pageSizes || DEFAULT_PAGE_SIZES}
          noRowsOverlayComponent={() => emptyText}
          loadingOverlayComponent={() => <QuoteLoader />}
          rowSelection={multiSelectRow ? "multiple" : "single"}
          isRowSelectable={isRowSelectable ? (node) => !!node.data && isRowSelectable(node.data) : undefined}
          includeHiddenColumnsInQuickFilter={true}
          onSelectionChanged={
            onSelectionChanged
              ? (event) => {
                  const rows = event.api.getSelectedRows();
                  onSelectionChanged?.(rows);
                }
              : undefined
          }
          animateRows={false}
          onModelUpdated={() =>
            agGridRef.current && agGridRef.current.api.getDisplayedRowCount() === 0
              ? agGridRef.current.api.showNoRowsOverlay()
              : agGridRef.current?.api.hideOverlay()
          }
          getRowStyle={
            onRowClicked ? (params) => ({ cursor: "pointer", ...(getRowStyle?.(params) || {}) }) : getRowStyle
          }
          onRowClicked={
            onRowClicked
              ? (event: RowClickedEvent<T>) => {
                  const target = event.event?.target as HTMLElement | null;
                  if (target?.closest("button, a, input, [role='menu'], .ag-selection-checkbox")) return;
                  if (event.data) onRowClicked(event.data);
                }
              : undefined
          }
          pinnedBottomRowData={pinnedBottomRowData}
          cacheQuickFilter
          quickFilterParser={quickFilterParser} // https://www.ag-grid.com/react-data-grid/filter-quick/#quick-filter-parser
        />
      </Box>

      <Stack gap={1}>
        {/* columns config */}
        {!hideColumnsConfig && <ColumnsConfig columns={columns} changeVisibility={changeVisibility} />}

        {/* csv export button */}
        {!hideCsvExport && <ExportToCSV exportToCSV={exportToCSV} disabled={rowData?.length <= 0} />}
      </Stack>
    </Stack>
  );
};

const LocalDataGrid = forwardRef(LocalDataGridInner) as <T>(
  props: Props<T> & { ref?: React.Ref<any> },
) => ReturnType<typeof LocalDataGridInner>;

export default LocalDataGrid;
